/**
 * buildCanvasContent — compose a Canvas document from a completed
 * Deep Explorer session.
 *
 * Input: the deliverable (if any), the plan, the activity log, the
 * list of retrieved sources.
 *
 * Output: a single markdown string with the following structure:
 *
 *   # <derived title>
 *
 *   <deliverable body — the agent's answer>
 *
 *   ---
 *
 *   ## Appendix: research plan
 *
 *   <plan.md body>
 *
 *   ## Appendix: sources consulted
 *
 *   <bulleted list with library + page + snippet>
 *
 *   ## Appendix: activity timeline
 *
 *   <first and last tool-call timestamps + top 10 by recency>
 *
 * The appendix sections are omitted when their source data is empty.
 */
import type { SourceCardData } from "../components/deep-explorer-side-panel/source-card-utils"
import type { DeepActivityEvent, DeepFile, DeepTodo } from "@/stores/ai-explorer-store"

// Inlined from SourceCard.tsx to keep this module React-free (so unit tests
// can run without bootstrapping a DOM). Keep in sync with the SourceCard original.
function parsePage(raw: string): number | null {
  const match = /\d+/.exec(raw)
  if (!match) return null
  const n = Number.parseInt(match[0], 10)
  return Number.isFinite(n) ? n : null
}

function sourceToCitation(source: SourceCardData): string | null {
  const pageNum = source.sourcepage ? parsePage(source.sourcepage) : null
  const page = pageNum != null ? `#page=${pageNum}` : ""
  switch (source.type) {
    case "library":
      return source.library && source.sourcefile
        ? `${source.library}:${source.sourcefile}${page}`
        : null
    case "web":
      return source.url ?? null
    case "m365":
      return source.url ?? source.id
    case "doc":
    case "other":
    default:
      if (source.url) return source.url
      if (source.sourcefile) return `${source.sourcefile}${page}`
      return null
  }
}

const DELIVERABLE_TYPE_COMMENT_RE = /<!--\s*deliverable_type\s*:\s*[a-z_]+\s*-->\s*/gi

/** Strip the internal deliverable_type comment so it doesn't appear in Canvas. */
function stripDeliverableMarker(content: string): string {
  return content.replace(DELIVERABLE_TYPE_COMMENT_RE, "").trimStart()
}

const PLACEHOLDER_HEADS = new Set(["", "source", "source:", "sources", "ref"])

/**
 * Deep Explorer occasionally emits citation tokens with a literal placeholder
 * head (`[[source|page=N|quote="..."]]`) instead of a resolvable
 * `library:filename` or URL. Those flow through to Canvas where the references
 * panel renders them as "source" / "source page 1" with no useful title
 * (bug 5 in bugerport_deepagents.pdf).
 *
 * Substitute the Nth distinct placeholder with the Nth source from the ribbon
 * so Canvas references resolve to a real title/URL. Citations that already
 * carry a real source pass through unchanged.
 */
function rewritePlaceholderCitations(
  content: string,
  sources: SourceCardData[] | undefined
): string {
  if (!sources || sources.length === 0) return content
  const seen = new Map<string, number>()
  let nextIndex = 0
  return content.replace(/\[\[([^\][]+)\]\]/g, (full, inner: string) => {
    const head = inner.split("|")[0].split("#")[0].trim().toLowerCase()
    if (!PLACEHOLDER_HEADS.has(head)) return full
    let mapped = seen.get(inner)
    if (mapped == null) {
      mapped = nextIndex
      seen.set(inner, mapped)
      nextIndex += 1
    }
    const ribbonEntry = sources.at(mapped)
    if (!ribbonEntry) return full
    const realSource = sourceToCitation(ribbonEntry)
    if (!realSource) return full
    const pipeIndex = inner.indexOf("|")
    const tail = pipeIndex >= 0 ? inner.slice(pipeIndex) : ""
    return `[[${realSource}${tail}]]`
  })
}

/**
 * Derive a document title — first H1 heading, else first non-empty line,
 * else the fallback.
 */
export function deriveTitleFromContent(content: string, fallback: string): string {
  const lines = content.split("\n").map((l) => l.trim())
  for (const line of lines) {
    if (line.startsWith("# ")) {
      return line.slice(2).trim().slice(0, 120) || fallback
    }
  }
  const firstNonEmpty = lines.find((l) => l.length > 0 && !l.startsWith("<!--"))
  if (firstNonEmpty) {
    return firstNonEmpty.replace(/^#+\s*/, "").slice(0, 120)
  }
  return fallback
}

export interface CanvasVerification {
  citation: string
  verified: boolean | null
  issue?: string
}

interface BuildCanvasInput {
  question: string
  deliverable?: DeepFile | null
  plan?: DeepFile | null
  todos?: DeepTodo[]
  activityLog?: DeepActivityEvent[]
  /** Retrieved sources from the session (typically from collectSourceCards).
   *  Rendered as an appendix so Canvas preserves the provenance that the
   *  Source Ribbon already shows live. */
  sources?: SourceCardData[]
  /** Fact-checker verdicts, if available. Included as an appendix so the
   *  archived dossier preserves the trust signal. */
  verifications?: CanvasVerification[]
  /** Aggregate summary (matches the Source Ribbon header). */
  verificationSummary?: {
    total: number
    verified: number
    unverified: number
  } | null
}

export interface CanvasPayload {
  title: string
  content: string
}

function formatTodoLine(todo: DeepTodo): string {
  const content = todo.content || todo.text || ""
  const marker = todo.status === "completed" ? "[x]" : "[ ]"
  return `- ${marker} ${content}`
}

function formatActivityLine(event: DeepActivityEvent): string {
  const time = event.timestamp.slice(11, 19)
  const tag = event.event === "tool_start" ? "→" : "✓"
  return `- \`${time}\` ${tag} ${event.tool}`
}

function formatSourceLine(source: SourceCardData, index: number): string {
  const n = index + 1
  const title = source.title.trim() || "Source"
  if (source.url) {
    return `- [${n}] [${title}](${source.url})`
  }
  if (source.sourcefile) {
    const page = source.sourcepage ? ` · page ${source.sourcepage}` : ""
    const library = source.library ? ` — ${source.library}` : ""
    return `- [${n}] ${source.sourcefile}${page}${library}`
  }
  return `- [${n}] ${title}`
}

export function buildCanvasContent(input: BuildCanvasInput): CanvasPayload {
  const deliverableBody = input.deliverable
    ? rewritePlaceholderCitations(
        stripDeliverableMarker(input.deliverable.content || ""),
        input.sources
      )
    : ""

  const title = deriveTitleFromContent(
    deliverableBody || input.question,
    input.question.slice(0, 120) || "Deep Explorer session"
  )

  const parts: string[] = []

  // The deliverable body becomes the document's primary content.
  if (deliverableBody) {
    parts.push(deliverableBody.trim())
  } else {
    // Fallback when no deliverable yet: include the plan body so the user
    // still gets something useful saved.
    parts.push(`# ${title}`, "", `> _Question:_ ${input.question}`)
  }

  // Appendix: plan
  if (input.plan && input.plan.content.trim()) {
    parts.push("", "---", "", "## Appendix · research plan", "")
    parts.push(stripDeliverableMarker(input.plan.content.trim()))
  }

  // Appendix: sources consulted — mirrors the Source Ribbon so Canvas has
  // the provenance per citation even after the live session is gone.
  if (input.sources && input.sources.length > 0) {
    parts.push("", "## Appendix · sources consulted", "")
    parts.push(input.sources.map(formatSourceLine).join("\n"))
  }

  // Appendix: todo list (always useful for audit)
  if (input.todos && input.todos.length > 0) {
    parts.push("", "## Appendix · steps executed", "")
    parts.push(input.todos.map(formatTodoLine).join("\n"))
  }

  // Appendix: activity timeline (compact: first + last + 10 most recent)
  if (input.activityLog && input.activityLog.length > 0) {
    const log = input.activityLog
    const recent = log.slice(-10)
    parts.push("", "## Appendix · activity timeline", "")
    parts.push(recent.map(formatActivityLine).join("\n"))
    parts.push("", `_${log.length} events total._`)
  }

  // Appendix: fact-checker verification summary — preserves the trust
  // signal in the archived dossier so readers can see at a glance which
  // citations were auto-verified and which weren't.
  const summary = input.verificationSummary
  const verdicts = input.verifications ?? []
  if (summary && summary.total > 0) {
    parts.push("", "## Appendix · citation verification", "")
    if (summary.unverified === 0) {
      parts.push(
        `**${summary.verified}/${summary.total}** citations verified ✓ — all quotes matched their sources.`
      )
    } else {
      parts.push(
        `**${summary.verified}/${summary.total}** citations verified · ` +
          `**${summary.unverified}** could not be confirmed.`
      )
    }
    const problems = verdicts.filter((v) => v.verified === false)
    if (problems.length > 0) {
      parts.push("", "### Unverified citations", "")
      for (const p of problems.slice(0, 20)) {
        const issue = p.issue ? ` — _${p.issue}_` : ""
        parts.push(`- \`${p.citation}\`${issue}`)
      }
      if (problems.length > 20) {
        parts.push(`- … and ${problems.length - 20} more.`)
      }
    }
  }

  const content = parts.join("\n").trim()

  return { title, content }
}
